import { z } from "zod";
// The contract of the privacy routes (CHT-771, Phase 13.5; docs/engineering/privacy-operations.md).
//
// CUSTOMER (the verified session names the customer; no path, query or body does):
//   POST /customer/privacy/exports                         ask for an export of your own data (starts approved, runs at once)
//   POST /customer/privacy/deletion-requests               ask for the deletion of your account (a staff decision follows)
//   GET  /customer/privacy/requests                        your own requests, newest first
//   POST /customer/privacy/requests/:requestId/cancel      withdraw a request nobody has started
//   GET  /customer/privacy/exports/:requestId/download     the export package (JSON file; not the envelope)
//
// STAFF (supervisor and administrator; the capability is checked before a body or a query is read):
//   GET  /staff/privacy/matrix                             `privacy.read`    the retention matrix: what is deleted, anonymized or kept, and why
//   GET  /staff/privacy/requests                           `privacy.read`    the list, with filters
//   GET  /staff/privacy/requests/:requestId                `privacy.read`    one request with its step ledger
//   POST /staff/privacy/requests                           `privacy.request` ask on behalf of a customer
//   POST /staff/privacy/requests/:requestId/decision       `privacy.approve` approve or reject (never your own request)
//   POST /staff/privacy/requests/:requestId/cancel         `privacy.request` withdraw a request nobody has started
//   POST /staff/privacy/requests/:requestId/retry          `privacy.approve` run a partially completed request again
//   GET  /staff/privacy/requests/:requestId/export         `privacy.download_export` the package of an export that staff asked for
//
// Every answer is `Cache-Control: no-store`. A view holds ids, closed codes, counts and times: no cleanup manifest, no provider id, no name.
// Every shape is STRICT. This package cannot import the domain: the tuples below repeat its tuples, and a parity test in `apps/api` fails when one
// differs.
export const PRIVACY_CUSTOMER_EXPORTS_PATH = "/customer/privacy/exports";
export const PRIVACY_CUSTOMER_DELETION_PATH = "/customer/privacy/deletion-requests";
export const PRIVACY_CUSTOMER_REQUESTS_PATH = "/customer/privacy/requests";
export const PRIVACY_CUSTOMER_REQUEST_CANCEL_PATH = "/customer/privacy/requests/:requestId/cancel";
export const PRIVACY_CUSTOMER_EXPORT_DOWNLOAD_PATH = "/customer/privacy/exports/:requestId/download";
export const STAFF_PRIVACY_MATRIX_PATH = "/staff/privacy/matrix";
export const STAFF_PRIVACY_REQUESTS_PATH = "/staff/privacy/requests";
export const STAFF_PRIVACY_REQUEST_PATH = "/staff/privacy/requests/:requestId";
export const STAFF_PRIVACY_DECISION_PATH = "/staff/privacy/requests/:requestId/decision";
export const STAFF_PRIVACY_CANCEL_PATH = "/staff/privacy/requests/:requestId/cancel";
export const STAFF_PRIVACY_RETRY_PATH = "/staff/privacy/requests/:requestId/retry";
export const STAFF_PRIVACY_EXPORT_PATH = "/staff/privacy/requests/:requestId/export";
export const privacyRequestKinds = ["EXPORT", "DELETE"];
export const privacyRequestStates = [
    "REQUESTED",
    "APPROVED",
    "EXECUTING",
    "COMPLETED",
    "PARTIALLY_COMPLETED",
    "REJECTED",
    "CANCELLED",
];
export const privacyRequesterKinds = ["customer", "staff"];
export const privacyReasonCodes = [
    "CUSTOMER_REQUEST",
    "STAFF_ASSISTED",
    "LEGAL_REQUEST",
    "TEST_CUSTOMER",
];
/** The reasons a staff user may give (the customer's own reason is never chosen by staff). */
export const privacyStaffReasonCodes = [
    "STAFF_ASSISTED",
    "LEGAL_REQUEST",
    "TEST_CUSTOMER",
];
export const privacyStepCodes = [
    "SUSPEND",
    "MEMORY",
    "PROFILE",
    "PREFERENCES",
    "SAVED",
    "TRIPS",
    "REQUESTS",
    "CONVERSATIONS",
    "NOTIFICATIONS",
    "EXPORTS",
    "ANALYTICS",
    "STREAM",
    "PUSH",
    "STORAGE",
    "AUTH",
    "ANALYTICS_VENDOR",
    "TOMBSTONE",
    "ASSEMBLE",
];
export const privacyStepStates = ["PENDING", "DONE", "FAILED"];
export const privacyStepErrorCodes = [
    "PROVIDER_UNAVAILABLE",
    "PROVIDER_TIMEOUT",
    "PROVIDER_REFUSED",
    "PROVIDER_NOT_CONFIGURED",
    "DATABASE_ERROR",
    "EXPORT_TOO_LARGE",
    "INTERNAL",
];
export const privacyRetentionActions = [
    "DELETED",
    "ANONYMIZED",
    "KEPT",
    "PROVIDER_DELETED",
    "NOT_HELD",
];
const id = z.guid().transform((value) => value.toLowerCase());
const instant = z.iso.datetime({ offset: true });
export const privacyStepViewSchema = z.strictObject({
    step: z.enum(privacyStepCodes),
    state: z.enum(privacyStepStates),
    attempts: z.number().int().min(0),
    affected: z.number().int().min(0).nullable(),
    lastErrorCode: z.enum(privacyStepErrorCodes).nullable(),
    completedAt: instant.nullable(),
});
export const privacyExportViewSchema = z.strictObject({
    available: z.boolean(),
    expiresAt: instant,
    byteSize: z.number().int().min(1),
    downloadsLeft: z.number().int().min(0),
});
const requestFields = {
    id,
    customerId: id,
    kind: z.enum(privacyRequestKinds),
    state: z.enum(privacyRequestStates),
    version: z.number().int().min(1),
    requesterKind: z.enum(privacyRequesterKinds),
    reasonCode: z.enum(privacyReasonCodes),
    createdAt: instant,
    decidedAt: instant.nullable(),
    startedAt: instant.nullable(),
    completedAt: instant.nullable(),
    lastErrorCode: z.enum(privacyStepErrorCodes).nullable(),
    steps: z.array(privacyStepViewSchema).max(20),
    export: privacyExportViewSchema.nullable(),
};
/** What the customer sees of a request of theirs. */
export const privacyRequestViewSchema = z.strictObject(requestFields);
export const privacyRequestListSchema = z.strictObject({
    items: z.array(privacyRequestViewSchema).max(100),
});
/** What staff see in addition: who asked and who decided (a staff user id; the customer id when the customer asked). */
export const staffPrivacyRequestViewSchema = z.strictObject({
    ...requestFields,
    requesterId: id,
    decidedByStaffId: id.nullable(),
});
export const staffPrivacyRequestListSchema = z.strictObject({
    items: z.array(staffPrivacyRequestViewSchema).max(100),
});
export const staffPrivacyRequestsQuerySchema = z.strictObject({
    customerId: id.optional(),
    kind: z.enum(privacyRequestKinds).optional(),
    state: z.enum(privacyRequestStates).optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
});
export const staffPrivacyCreateBodySchema = z.strictObject({
    customerId: id,
    kind: z.enum(privacyRequestKinds),
    reasonCode: z.enum(privacyStaffReasonCodes),
});
export const staffPrivacyDecisionBodySchema = z.strictObject({
    expectedVersion: z.number().int().min(1),
    decision: z.enum(["APPROVE", "REJECT"]),
});
export const staffPrivacyVersionBodySchema = z.strictObject({
    expectedVersion: z.number().int().min(1),
});
/** One row of the retention matrix (the same data the deletion follows and the document prints). */
export const privacyRetentionRowSchema = z.strictObject({
    dataClass: z.string().regex(/^[A-Z][A-Z0-9_]{1,63}$/),
    label: z.string().min(1).max(200),
    holder: z.string().min(1).max(400),
    action: z.enum(privacyRetentionActions),
    step: z.enum(privacyStepCodes).nullable(),
    why: z.string().min(1).max(1200),
    retention: z.string().min(1).max(600).nullable(),
    legalOpen: z.boolean(),
});
export const privacyRetentionMatrixSchema = z.strictObject({
    rows: z.array(privacyRetentionRowSchema).max(80),
    /** The steps of a deletion, in order, with the phase of each (`database` or `provider`). */
    steps: z
        .array(z.strictObject({ step: z.enum(privacyStepCodes), phase: z.enum(["database", "provider"]) }))
        .max(20),
    limits: z.strictObject({
        exportMaxBytes: z.number().int().min(1),
        exportTtlHours: z.number().int().min(1),
        exportDailyLimit: z.number().int().min(1),
        exportMaxDownloads: z.number().int().min(1),
    }),
});
